import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { CgAttachment } from 'react-icons/cg'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import useApiService from 'services/api'
import CreatableSelect from 'components/FormUtils/CreatableSelect'
import {
  CcBccToggles,
  ComposeMode,
  emailRecipientStyles,
  filterSelf,
  formatEmailRecipientOption,
  looksLikeEmail,
  RecipientRow,
  ReplyMode,
} from 'components/FormUtils/EmailRecipientSelect'
import { InlineComposeShell, InlineReplyHeader } from 'components/Email'
import { TextInput } from 'components/FormUtilsV2/TextInput/text-input'
import { StyledEditorRHF } from 'components/Editor/EditorRHF'
import {
  AttachmentList,
  Attachment,
  isNewAttachment,
} from 'components/Attachments/AttachmentList'
import {
  ALLOWED_EMAIL_ATTACHMENT_TYPES,
  MAX_EMAIL_ATTACHMENT_SIZE,
  EMAIL_ATTACHMENT_WARNING_THRESHOLD,
  getAcceptString,
} from 'constants/fileTypes'
import { useFileAttachments } from 'hooks/useFileAttachments'
import { useRecipientOptions } from 'hooks/useRecipientOptions'
import { cn } from 'components/UI/cn'
import { useAuth } from 'context/authentication/useAuth'
import {
  EmailContact,
  EmailMessage,
  EmailThread,
} from 'models/emailIntegration'
import { CATEGORY_BADGE_CLASS, DealBadge } from './DealBadge'

export type { ComposeMode }

/** Payload passed from ComposePanel to parent for optimistic send. */
export interface SendPayload {
  optimisticMessage: EmailMessage
  formData: FormData
  fromId: number
}

/** Create a chip from free-text input, rejecting anything that doesn't look
 *  like an email (second line of defense — menu-level `isValidNewOption` is
 *  the first). */
const createEmailOption = async (inputValue: string) => {
  if (!looksLikeEmail(inputValue)) return null
  return { label: inputValue, value: inputValue }
}

interface ComposePanelProps {
  mode: ComposeMode
  thread?: EmailThread
  replyToMessage?: EmailMessage
  integrationEmail?: string
  onSend: (payload: SendPayload) => void
  onDiscard: () => void
  onModeChange?: (mode: ComposeMode) => void
  dealIdentifier?: string | null
  dealName?: string | null
  companyName?: string
  /** When true, renders without its own container border/padding (parent provides the chrome). */
  standalone?: boolean
}

const composeSchema = z.object({
  to: z.array(z.string().email()).min(1, 'At least one recipient is required'),
  cc: z.array(z.string().email()).optional(),
  bcc: z.array(z.string().email()).optional(),
  from_id: z
    .number({ message: 'Please select a from email' })
    .min(1, 'Please select a from email'),
  subject: z.string().min(1, 'Subject is required'),
  body: z.string().min(1, 'Message body is required'),
})

type ComposeFormValues = z.infer<typeof composeSchema>

/** Accessor for the shared `filterSelf` against EmailContact shape. */
const getContactEmail = (c: EmailContact) => c.email

/** Build the default "To" recipients based on compose mode. */
function getDefaultTo(
  mode: ComposeMode,
  replyToMessage?: EmailMessage,
  integrationEmail?: string
): string[] {
  if (mode === 'compose' || mode === 'forward') return []

  const sender = replyToMessage?.sender || []
  const senderEmails = filterSelf(
    sender,
    integrationEmail,
    getContactEmail
  ).map((c) => c.email)

  // When replying to your own outbound message, address the original recipients
  // instead of yourself (filterSelf would leave an empty list).
  const fallbackTo = filterSelf(
    replyToMessage?.to || [],
    integrationEmail,
    getContactEmail
  ).map((c) => c.email)

  if (mode === 'reply') {
    return senderEmails.length > 0 ? senderEmails : fallbackTo
  }

  // reply_all: original sender + original To recipients (minus self)
  // Original Cc recipients go to getDefaultCc
  return [...new Set([...senderEmails, ...fallbackTo])]
}

/** Build the default "Cc" recipients based on compose mode. */
function getDefaultCc(
  mode: ComposeMode,
  replyToMessage?: EmailMessage,
  integrationEmail?: string
): string[] {
  if (mode !== 'reply_all') return []
  return filterSelf(
    replyToMessage?.cc || [],
    integrationEmail,
    getContactEmail
  ).map((c) => c.email)
}

/** Build default subject — no stacking of Re:/Fwd: per RFC 5322. */
function getDefaultSubject(
  mode: ComposeMode,
  thread?: EmailThread,
  replyToMessage?: EmailMessage
): string {
  const originalSubject = replyToMessage?.subject || thread?.subject || ''

  if (mode === 'compose') return ''

  if (mode === 'forward') {
    // Don't stack Fwd:, but preserve any existing Re: prefix
    if (/^Fwd:/i.test(originalSubject)) return originalSubject
    return `Fwd: ${originalSubject}`
  }

  // reply / reply_all — don't stack Re:
  if (/^Re:/i.test(originalSubject)) return originalSubject
  return `Re: ${originalSubject}`
}

/** Format an email contact list as "Name <email>, ..." for display in quoted headers. */
function formatContactList(contacts: EmailContact[] | null): string {
  if (!contacts?.length) return ''
  return contacts
    .map((c) =>
      c.name ? `${c.name} &lt;${c.email}&gt;` : `&lt;${c.email}&gt;`
    )
    .join(', ')
}

/** Build default body (quoted original for forward, Gmail-style header). */
function getDefaultBody(
  mode: ComposeMode,
  replyToMessage?: EmailMessage
): string {
  if (mode !== 'forward' || !replyToMessage?.body) return ''

  const from = formatContactList(replyToMessage.sender)
  const date = replyToMessage.date
    ? new Date(replyToMessage.date).toLocaleString()
    : ''
  const to = formatContactList(replyToMessage.to)
  const cc = formatContactList(replyToMessage.cc)

  const headerLines = [
    `From: ${from || 'Unknown'}`,
    `Date: ${date}`,
    `Subject: ${replyToMessage.subject || ''}`,
    `To: ${to}`,
  ]
  if (cc) headerLines.push(`Cc: ${cc}`)

  return `<br/><br/><div style="border-left:3px solid #ccc;padding-left:12px;color:#555">
<p>---------- Forwarded message ---------</p>
<p>${headerLines.join('<br/>')}</p>
<br/>
${replyToMessage.body}
</div>`
}

/** Thin alias so we can spread shared props into To/Cc/Bcc recipient fields. */
const RecipientSelect = CreatableSelect

/** Whether the compose mode is inline within a thread (not standalone). */
function isInlineMode(mode: ComposeMode): boolean {
  return mode === 'reply' || mode === 'reply_all' || mode === 'forward'
}

/** Compose panel for reply, reply all, forward, and new compose. */
export function ComposePanel({
  mode,
  thread,
  replyToMessage,
  integrationEmail,
  onSend,
  onDiscard,
  onModeChange,
  dealIdentifier,
  dealName,
  companyName,
  standalone,
}: ComposePanelProps) {
  const api = useApiService()
  const { user } = useAuth()
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [showCc, setShowCc] = useState(() => {
    if (mode === 'reply_all') {
      const cc = getDefaultCc(mode, replyToMessage, integrationEmail)
      return cc.length > 0
    }
    return false
  })
  const [showBcc, setShowBcc] = useState(false)
  const inline = isInlineMode(mode)

  const {
    fileInputRef,
    openFileSelect,
    handleFileSelect,
    createRemoveHandler,
  } = useFileAttachments({
    allowedTypes: ALLOWED_EMAIL_ATTACHMENT_TYPES,
    maxFileSize: MAX_EMAIL_ATTACHMENT_SIZE,
    maxTotalSize: MAX_EMAIL_ATTACHMENT_SIZE,
    onAdd: (newAttachments) =>
      setAttachments((prev) => [...prev, ...newAttachments]),
  })

  const { data: emailIntegrationOptions } =
    api.emailIntegration.useGetEmailIntegrationOptions()

  const defaultTo = useMemo(
    () => getDefaultTo(mode, replyToMessage, integrationEmail),
    [mode, replyToMessage, integrationEmail]
  )
  const defaultCc = useMemo(
    () => getDefaultCc(mode, replyToMessage, integrationEmail),
    [mode, replyToMessage, integrationEmail]
  )
  const defaultSubject = useMemo(
    () => getDefaultSubject(mode, thread, replyToMessage),
    [mode, thread, replyToMessage]
  )
  const defaultBody = useMemo(
    () => getDefaultBody(mode, replyToMessage),
    [mode, replyToMessage]
  )

  const { control, handleSubmit, setValue, register, getValues } =
    useForm<ComposeFormValues>({
      defaultValues: {
        to: defaultTo,
        cc: defaultCc,
        bcc: [],
        from_id: 0,
        subject: defaultSubject,
        body: defaultBody,
      },
      resolver: zodResolver(composeSchema),
      mode: 'onSubmit',
    })

  // In inline reply/reply_all mode the To field isn't rendered (a compact
  // header is shown instead), so register it manually for validation.
  // Also register cc for reply_all so default Cc values are submitted.
  useEffect(() => {
    if (inline && mode !== 'forward') {
      register('to')
      setValue('to', defaultTo)
    }
    if (inline && mode === 'reply_all' && defaultCc.length > 0) {
      register('cc')
      setValue('cc', defaultCc)
    }
  }, [inline, mode, defaultTo, defaultCc, register, setValue])

  // Select the email integration matching the thread's integration email,
  // falling back to the first available integration
  useEffect(() => {
    if (!emailIntegrationOptions?.length) return
    const match = integrationEmail
      ? emailIntegrationOptions.find(
          (opt) => opt.label.toLowerCase() === integrationEmail.toLowerCase()
        )
      : undefined
    setValue('from_id', match?.value ?? emailIntegrationOptions[0].value)
  }, [emailIntegrationOptions, integrationEmail, setValue])

  // Thread-context contacts surfaced in the autocomplete unconditionally.
  const extraContacts = useMemo<EmailContact[]>(
    () => [
      ...(replyToMessage?.sender ?? []),
      ...(replyToMessage?.to ?? []),
      ...(replyToMessage?.cc ?? []),
    ],
    [replyToMessage]
  )

  const {
    options: recipientOptions,
    isLoading: isLoadingRecipients,
    setSearch: setRecipientSearch,
  } = useRecipientOptions(extraContacts)

  const handleRemoveAttachment = createRemoveHandler((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  })

  const attachmentListProps = {
    attachments,
    onRemove: handleRemoveAttachment,
    maxSize: MAX_EMAIL_ATTACHMENT_SIZE,
    warningThreshold: EMAIL_ATTACHMENT_WARNING_THRESHOLD,
  }

  /** Shared props for To/Cc/Bcc recipient selects. */
  const recipientSelectProps = useMemo(
    () => ({
      placeholder: 'Search contacts or team members...',
      isMulti: true as const,
      options: recipientOptions,
      onSearchChange: setRecipientSearch,
      isLoading: isLoadingRecipients,
      className: 'mt-0',
      onCreateOption: createEmailOption,
      isValidNewOption: looksLikeEmail,
      styles: emailRecipientStyles,
      formatOptionLabel: formatEmailRecipientOption,
    }),
    [recipientOptions, isLoadingRecipients, setRecipientSearch]
  )

  /** Resolve a display name from recipientOptions for a given email. */
  const resolveContactName = (email: string): string => {
    const opt = recipientOptions.find((o) => o.value === email)
    if (!opt?.label?.includes('<')) return ''
    return opt.label.split('<')[0].trim()
  }

  /** Map an array of email strings to {name, email} contact objects. */
  const buildRecipients = (emails: string[]) =>
    emails.map((email) => ({ name: resolveContactName(email), email }))

  const onSubmit = handleSubmit((values) => {
    // Prevent sending with uninitialized from_id
    if (!values.from_id) return

    const toRecipients = buildRecipients(values.to)
    const ccRecipients = values.cc?.length ? buildRecipients(values.cc) : null
    const bccRecipients = values.bcc?.length
      ? buildRecipients(values.bcc)
      : null

    // Build FormData for the API call
    const formData = new FormData()
    formData.append('to', JSON.stringify(toRecipients))
    formData.append('subject', values.subject)
    formData.append('body', values.body)

    if (
      (mode === 'reply' || mode === 'reply_all') &&
      replyToMessage?.email_id
    ) {
      formData.append('reply_to_message_id', replyToMessage.email_id)
    }

    if (ccRecipients) formData.append('cc', JSON.stringify(ccRecipients))
    if (bccRecipients) formData.append('bcc', JSON.stringify(bccRecipients))

    attachments.forEach((attachment) => {
      if (isNewAttachment(attachment)) {
        formData.append('attachments', attachment.file)
      }
    })

    // Build optimistic message from form values
    const optimisticMessage: EmailMessage = {
      id: -Date.now(),
      outbound: true,
      related_contacts: [],
      sender: [
        {
          name: `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim(),
          email: integrationEmail || '',
        },
      ],
      to: toRecipients,
      cc: ccRecipients,
      subject: values.subject,
      snippet: values.body.replace(/<[^>]*>/g, '').slice(0, 100),
      date: new Date().toISOString(),
      body: values.body,
      attachments: [],
    }

    onSend({ optimisticMessage, formData, fromId: values.from_id })
  })

  /** Summarize who the reply is addressed to for the compact inline header. */
  const inlineRecipientLabel = useMemo(() => {
    if (!defaultTo.length) return ''

    const resolveLabel = (email: string) => {
      const opt = recipientOptions.find((o) => o.value === email)
      if (!opt?.label) return email
      // Show name only (strip "<email>" suffix) like Gmail
      const name = opt.label.split('<')[0].trim()
      return name || email
    }

    // Show up to 2 names before collapsing to +N
    if (defaultTo.length <= 2) {
      return defaultTo.map(resolveLabel).join(', ')
    }
    const firstLabel = resolveLabel(defaultTo[0])
    return `${firstLabel} (+${defaultTo.length - 1})`
  }, [defaultTo, recipientOptions])

  const userInitials =
    [user?.first_name, user?.last_name]
      .filter(Boolean)
      .map((n) => n![0].toUpperCase())
      .join('')
      .slice(0, 2) || 'UN'

  const sendDisabled = !emailIntegrationOptions?.length
  const sendButton = (
    <button
      type="button"
      disabled={sendDisabled}
      onClick={() => onSubmit()}
      className={cn(
        'h-8 px-3.5 rounded-md text-sm font-medium text-white whitespace-nowrap',
        sendDisabled
          ? 'bg-surface-disabled cursor-not-allowed'
          : 'bg-brand hover:bg-brand-hover active:bg-brand-pressed'
      )}
    >
      Send
    </button>
  )

  const toolbarRight = (
    <div className="flex items-center gap-1.5">
      {dealIdentifier && (
        <DealBadge dealIdentifier={dealIdentifier} dealName={dealName} />
      )}
      {!inline && companyName && (
        <span className={CATEGORY_BADGE_CLASS}>{companyName}</span>
      )}
      <button
        type="button"
        onClick={onDiscard}
        className="text-xs font-medium text-fg-secondary underline decoration-solid hover:text-fg whitespace-nowrap"
      >
        Cancel
      </button>
    </div>
  )

  // Null when both toggles are already revealed so `RecipientRow` doesn't
  // render an empty trailing-slot wrapper that still eats `gap-2`.
  const ccBccToggles =
    showCc && showBcc ? null : (
      <CcBccToggles
        showCc={showCc}
        showBcc={showBcc}
        onShowCc={() => setShowCc(true)}
        onShowBcc={() => setShowBcc(true)}
      />
    )

  /** Collapse empty CC/BCC fields when the Quill editor content area gains focus. */
  const collapseEmptyRecipientFields = (e: React.FocusEvent) => {
    // Only collapse when the actual editor content (.ql-editor) is focused,
    // not toolbar buttons (Send, Cancel, attachment, formatting).
    const target = e.target as HTMLElement
    if (!target.closest('.ql-editor')) return
    const values = getValues()
    if (showCc && (!values.cc || values.cc.length === 0)) setShowCc(false)
    if (showBcc && (!values.bcc || values.bcc.length === 0)) setShowBcc(false)
  }

  /** Shared compose fields (To/Cc/Bcc/Subject/Body/Attachments). */
  const composeFields = (
    <>
      {/* Compact inline header for reply/reply_all (not forward) */}
      {inline && mode !== 'forward' && (
        <InlineReplyHeader
          mode={mode as ReplyMode}
          label={inlineRecipientLabel}
          onModeChange={onModeChange as ((m: ReplyMode) => void) | undefined}
          trailing={ccBccToggles}
        />
      )}

      <div
        className={cn(
          inline ? 'px-3 pt-3 flex-1 min-h-0 overflow-y-auto' : '',
          standalone && 'flex-1 min-h-0 flex flex-col'
        )}
      >
        {/* To field for forward and standalone compose */}
        {(mode === 'forward' || !inline) &&
          (standalone || inline ? (
            <RecipientRow label="To" trailing={ccBccToggles}>
              <RecipientSelect
                label={undefined}
                control={control}
                name="to"
                {...recipientSelectProps}
                placeholder=""
              />
            </RecipientRow>
          ) : (
            <>
              <RecipientSelect
                label="To"
                control={control}
                name="to"
                {...recipientSelectProps}
              />
              {ccBccToggles}
            </>
          ))}

        {/* Cc field */}
        {showCc &&
          (standalone || inline ? (
            <RecipientRow
              label="Cc"
              onRemove={() => {
                setShowCc(false)
                setValue('cc', [])
              }}
            >
              <RecipientSelect
                label={undefined}
                control={control}
                name="cc"
                {...recipientSelectProps}
                placeholder=""
              />
            </RecipientRow>
          ) : (
            <RecipientSelect
              label="Cc"
              control={control}
              name="cc"
              {...recipientSelectProps}
            />
          ))}

        {/* Bcc field */}
        {showBcc &&
          (standalone || inline ? (
            <RecipientRow
              label="Bcc"
              onRemove={() => {
                setShowBcc(false)
                setValue('bcc', [])
              }}
            >
              <RecipientSelect
                label={undefined}
                control={control}
                name="bcc"
                {...recipientSelectProps}
                placeholder=""
              />
            </RecipientRow>
          ) : (
            <RecipientSelect
              label="Bcc"
              control={control}
              name="bcc"
              {...recipientSelectProps}
            />
          ))}

        {/* Subject — only for standalone compose and forward */}
        {!inline &&
          (standalone ? (
            <div className="py-1 border-b border-line-subtle [&_.border-input]:!border-0">
              <TextInput
                control={control}
                name="subject"
                type="text"
                label={undefined}
                placeholder="Subject"
                noMargin
              />
            </div>
          ) : (
            <TextInput
              control={control}
              name="subject"
              type="text"
              label="Subject"
              placeholder="Subject"
              noMargin
              className="[&_label]:text-fg-muted"
            />
          ))}

        {/* Body editor */}
        <div
          onFocusCapture={collapseEmptyRecipientFields}
          className={cn(
            inline ? 'mt-0' : 'mt-2',
            standalone &&
              'flex-1 min-h-0 flex flex-col [&>div]:flex-1 [&>div]:min-h-0 [&>div]:flex [&>div]:flex-col [&>div>div]:flex-1 [&>div>div]:min-h-0 [&>div>div]:flex [&>div>div]:flex-col'
          )}
        >
          <StyledEditorRHF
            name="body"
            borderless={inline}
            className={cn(
              !standalone &&
                'min-h-28 [&_.ql-container]:min-h-28 [&_.ql-editor]:min-h-28',
              inline && '[&_.ql-editor]:!py-2 [&_.ql-editor]:!px-2',
              standalone && [
                'flex-1 flex flex-col min-h-0',
                '[&_.ql-container]:flex-1 [&_.ql-container]:flex [&_.ql-container]:flex-col [&_.ql-container]:min-h-0 [&_.ql-container]:!border-0',
                '[&_.ql-editor]:flex-1 [&_.ql-editor]:min-h-0 [&_.ql-editor]:!overflow-y-auto',
                '[&_.ql-toolbar]:!border-0',
              ]
            )}
            control={control}
            placeholder={inline ? '' : 'Write your message...'}
            toolbarPrefix={sendButton}
            toolbarSuffix={toolbarRight}
            toolbarClassName={
              inline ? 'sticky bottom-0 bg-surface z-10' : undefined
            }
            additionalToolbarButtons={[
              <CgAttachment
                key="attachments"
                size={24}
                className="p-1 w-8 cursor-pointer hover:text-brand"
                onClick={openFileSelect}
              />,
            ]}
          />
        </div>

        {/* Attachments — inside scroll area for standalone, outside for inline */}
        {!inline && <AttachmentList {...attachmentListProps} />}
      </div>
      {inline && attachments.length > 0 && (
        <div className="px-3 pb-2 flex-shrink-0">
          <AttachmentList {...attachmentListProps} />
        </div>
      )}
    </>
  )

  return (
    <>
      {inline ? (
        <InlineComposeShell userInitials={userInitials}>
          {composeFields}
        </InlineComposeShell>
      ) : (
        <div
          className={cn(
            standalone
              ? 'flex flex-col flex-1 min-h-0 px-4 pt-7 pb-4'
              : // Embedded path (mode="compose", no standalone prop): currently has no
                // callers — both active callers pass standalone. Parent is expected
                // to provide container chrome (border/shadow/padding) if this is ever used.
                'p-4 mt-3 bg-surface'
          )}
        >
          {composeFields}
        </div>
      )}

      <input
        type="file"
        hidden
        multiple
        ref={fileInputRef}
        onChange={(e) => handleFileSelect(e, attachments)}
        accept={getAcceptString(ALLOWED_EMAIL_ATTACHMENT_TYPES)}
      />
    </>
  )
}
